import { inject, Injectable } from "@angular/core";
import { combineLatest, Observable } from "rxjs";
import { debounceTime, filter, map, startWith } from "rxjs/operators";

import { GeoService } from "../../../viewer-components/services/internal-services/geo.service";
import { VIEWER_SERVICE } from "../../../viewer-components/services/internal-services/viewer-services/inject-base-viewer.service";
import type { IProjectAOI } from "../../../viewer-components/interfaces/i-project-aoi";
import type { ProjectStatus } from "../../../viewer-components/model/project-status";
import type { Viewerlayer } from "../../../viewer-components/model/viewerlayer";

/** Zoom threshold above which all enabledAOIs are sent; below it only centerAOI is sent. */
const COLLECTIONS_FULL_ZOOM = 16;

export interface AiCollection {
  id: string;
  name: string;
  layer: string;
  oriented: boolean;
  product_type: string;
  year: string;
}

/** A user layer as sent to the AI agent — GROUPLAYERs carry their children under `childs`, same shape. */
export interface AiOverlayLayer {
  id: string;
  name: string;
  file_type: string;
  childs?: AiOverlayLayer[];
}

/**
 * Slim map context object sent to the AI agent as context.
 * To add a new field: add it here, populate it in #build() below, and update the
 * `description` string in AgentKitService.updateMapContext() so the agent knows it exists.
 */
export interface AiMapContext {
  center: { lat: number; lng: number };
  bbox: [number, number, number, number];
  zoom: number;
  orientation: string;
  mode: string;
  /** All enabled collections visible at current zoom (or just centerAOI when zoom < 16). */
  availableCollections: AiCollection[];
  /** The collection at the map center — always present when a collection is active. */
  selectedCollection: AiCollection | null;
  /** User-uploaded vector layers (GeoJSON, KML, GPX…). Excludes service layers (WFS, WMS). */
  overlayLayers: AiOverlayLayer[];
}

/**
 * Builds a slim AiMapContext from BaseViewerService.projectStatus$ and userLayers$.
 * ProjectStatus embeds MapStatus so a single subscription covers map + collection state.
 * MapStatus coordinates are Web Mercator — GeoService.toWgs84() is applied here.
 * userLayers$ contains only user-uploaded vector layers (layerType === "VECTOR").
 */
@Injectable()
export class MapContextBuilderService {
  private readonly viewerService = inject(VIEWER_SERVICE);

  readonly context$: Observable<AiMapContext> = combineLatest([
    this.viewerService.projectStatus$.pipe(filter((s): s is ProjectStatus => !!s)),
    this.viewerService.userLayers$.pipe(startWith([] as Viewerlayer[])),
  ]).pipe(
    debounceTime(300),
    map(([projectStatus, userLayers]) => this.#build(projectStatus, userLayers)),
  );

  #build(projectStatus: ProjectStatus, userLayers: Viewerlayer[]): AiMapContext {
    const ms = projectStatus.mapStatus;

    // MapStatus is always in Web Mercator (EPSG:3857) — convert to WGS84.
    const centerWgs84 = ms?.center ? GeoService.toWgs84(ms.center) : null;
    const extentWgs84 = ms?.extent ? GeoService.toWgs84(ms.extent) : null;

    const centerCoords = (centerWgs84?.geometry as any)?.coordinates as [number, number] | undefined;
    const polyCoords = (extentWgs84?.geometry as any)?.coordinates?.[0] as [number, number][] | undefined;

    const zoom = ms?.zoom ?? 0;

    const bbox: [number, number, number, number] = polyCoords
      ? [
          Math.min(...polyCoords.map((c) => c[0])),
          Math.min(...polyCoords.map((c) => c[1])),
          Math.max(...polyCoords.map((c) => c[0])),
          Math.max(...polyCoords.map((c) => c[1])),
        ]
      : [0, 0, 0, 0];

    const selectedCollection = projectStatus.centerAOI ? this.#toAiCollection(projectStatus.centerAOI) : null;

    // At zoom < 16 sending all enabled AOIs would be too much data — send only the center one.
    const aoiSource =
      zoom >= COLLECTIONS_FULL_ZOOM
        ? projectStatus.enabledAOIs ?? []
        : projectStatus.centerAOI
        ? [projectStatus.centerAOI]
        : [];

    return {
      center: centerCoords ? { lng: centerCoords[0], lat: centerCoords[1] } : { lat: 0, lng: 0 },
      bbox,
      zoom,
      orientation: ms.orientation,
      mode: ms.is3dMode ? "3D" : "2D",
      availableCollections: aoiSource.map((aoi) => this.#toAiCollection(aoi)),
      selectedCollection,
      overlayLayers: userLayers.map((l) => this.#toAiOverlayLayer(l)),
      // New field goes here — populate it in this returned object.
    };
  }

  #toAiOverlayLayer(l: Viewerlayer): AiOverlayLayer {
    return {
      id: String(l.id),
      name: l.pretty_name ?? l.name,
      file_type: l.overlayType ?? l.layerType ?? "",
      ...(l.childs?.length ? { childs: l.childs.map((c) => this.#toAiOverlayLayer(c)) } : {}),
    };
  }

  #toAiCollection(aoi: IProjectAOI): AiCollection {
    return {
      id: aoi.getCollectionId?.() ?? aoi.getLayerId?.() ?? aoi.name,
      name: aoi.getPrettyName?.() ?? aoi.getPrettyCollectionName?.() ?? aoi.name,
      layer: aoi.getLayerName?.() ?? aoi.layerName ?? aoi.getLayer?.() ?? "",
      oriented: aoi.isAOIOblique?.() ?? false,
      product_type: aoi.getCoverageType?.() ?? aoi.coverageType ?? "",
      year: aoi.getYear?.() ?? String(aoi.nadirDate?.getFullYear() ?? ""),
    };
  }
}
